import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupLabel,
} from "@babackapp/customer-ui/components/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/customer-ui/components/select";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import {
  ArchiveXIcon,
  BoxIcon,
  Loader2Icon,
  LoaderIcon,
  SearchIcon,
  SlidersHorizontalIcon,
} from "lucide-react";
import { motion } from "motion/react";
import { Controller } from "react-hook-form";
import { useFocusFirstError } from "@babackapp/return-widget-components/hooks/use-focus-first-error";
import { useTranslation } from "react-i18next";
import { useOrderIdentificationForm, type OrderIdentificationMode } from "../hooks/use-order-identification-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useNavigate, useParams } from "react-router-dom";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import {
  buildSecondaryInputIcon,
  buildSecondaryInputLabel,
} from "../utils/order-identification";
import { resolveOrderIdentificationError } from "../utils/order-identification-error";

const FIELDS_IN_ORDER = ["orderName", "secondaryInput"] as const;
const ORDER_NAME_ERROR_ID = "order-identification-order-name-error";
const SECONDARY_INPUT_ERROR_ID = "order-identification-secondary-input-error";

export function ReturnWidgetOrderIdentification() {
  const { form, mutation, onSubmit, logoUrl, isMerchant, showSecondaryInput, shop, mode, setMode, validationInfo, isBypassing } = useOrderIdentificationForm();
  const navigate = useNavigate();
  const { shopId } = useParams<{ shopId: string }>();
  const shopName = shop.name;
  const t = useCustomText();
  const { i18n } = useTranslation("return-widget-v3");
  useFocusFirstError(form, FIELDS_IN_ORDER);

  if (isBypassing) {
    return (
      <WidgetPage variant="centered">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mx-auto"
        >
          <Loader2Icon className="size-10 animate-spin text-muted-foreground" />
        </motion.span>
      </WidgetPage>
    );
  }

  const errorInfo = mutation.error
    ? resolveOrderIdentificationError({
        error: mutation.error,
        t,
        userPortal: shop.preferences?.userPortal,
        language: i18n.language ?? "en",
      })
    : null;

  const orderNumberLabel = t("orderIdentification.fields.orderNumber");
  const secondaryInputLabel = buildSecondaryInputLabel(validationInfo, t);
  const SecondaryInputIcon = buildSecondaryInputIcon(validationInfo);

  return (
    <WidgetPage variant="centered">
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>{t("orderIdentification.title", { shopName })}</HeaderTitle>
        <HeaderDescription>
          {t("orderIdentification.description", { shopName })}
        </HeaderDescription>
      </Header>

      <UserPortalCallout texts={shop.preferences?.userPortal?.headerMessage} />

      {errorInfo && (
        <Alert variant="destructive">
          <ArchiveXIcon />
          {errorInfo.html ? (
            <AlertDescription dangerouslySetInnerHTML={{ __html: errorInfo.html }} />
          ) : (
            <>
              <AlertTitle>{errorInfo.title}</AlertTitle>
              <AlertDescription>{errorInfo.description}</AlertDescription>
            </>
          )}
        </Alert>
      )}

      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <Controller
          control={form.control}
          name="orderName"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-1.5">
              <InputGroup className="h-auto">
                <InputGroupInput
                  placeholder={`${shop.orderPrefix}00001`}
                  aria-required
                  aria-invalid={fieldState.invalid || undefined}
                  aria-describedby={fieldState.invalid ? ORDER_NAME_ERROR_ID : undefined}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{orderNumberLabel}</InputGroupLabel>
                  <BoxIcon />
                </InputGroupAddon>
              </InputGroup>
              {fieldState.invalid && (
                <FieldRequiredMessage id={ORDER_NAME_ERROR_ID} field={orderNumberLabel} />
              )}
            </div>
          )}
        />
        {showSecondaryInput && (
          <Controller
            control={form.control}
            name="secondaryInput"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1.5">
                <InputGroup className="h-auto">
                  <InputGroupInput
                    placeholder={secondaryInputLabel}
                    aria-required={!isMerchant || undefined}
                    aria-invalid={fieldState.invalid || undefined}
                    aria-describedby={fieldState.invalid ? SECONDARY_INPUT_ERROR_ID : undefined}
                    {...field}
                  />
                  <InputGroupAddon align="block-start">
                    <InputGroupLabel>{secondaryInputLabel}</InputGroupLabel>
                    <SecondaryInputIcon />
                  </InputGroupAddon>
                </InputGroup>
                {fieldState.invalid && (
                  <FieldRequiredMessage id={SECONDARY_INPUT_ERROR_ID} field={secondaryInputLabel} />
                )}
              </div>
            )}
          />
        )}
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            variants={{}}
          >
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <LoaderIcon className="animate-spin" />
              ) : (
                <>
                  <SearchIcon />
                  {t("orderIdentification.cta")}
                </>
              )}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
      {shop.giftCode?.activated && (
        <button
          type="button"
          className="font-content text-sm text-muted-foreground hover:text-foreground underline mt-2 cursor-pointer"
          onClick={() => navigate(`/return-widget/${shopId}/gift`)}
        >
          {t("gift.iReturnGift")}
        </button>
      )}
      <div className="flex items-center justify-end gap-4">
        {isMerchant && (
          <ModeSelector
            value={mode}
            onChange={setMode}
            normalLabel={t("orderIdentification.mode.normal")}
            retractationLabel={t("orderIdentification.mode.retractation")}
          />
        )}
      </div>
    </WidgetPage>
  );
}

/**
 * The only rule on these fields is "not empty", so the message names the field rather
 * than relaying the schema's French-only text. Read through the field's
 * aria-describedby when the submit focuses it (BBS-436).
 */
function FieldRequiredMessage({ id, field }: { id: string; field: string }) {
  const t = useCustomText();
  return (
    <p id={id} className="px-0.5 text-sm text-destructive">
      {t("orderIdentification.error.fieldRequired", { field })}
    </p>
  );
}

type ModeSelectorProps = {
  value: OrderIdentificationMode;
  onChange: (value: OrderIdentificationMode) => void;
  normalLabel: string;
  retractationLabel: string;
};

function ModeSelector({ value, onChange, normalLabel, retractationLabel }: ModeSelectorProps) {
  return (
    <div className="flex items-center gap-1.5 text-muted-foreground">
      <SlidersHorizontalIcon className="size-3.5 shrink-0" />
      <Select value={value} onValueChange={(v) => onChange(v as OrderIdentificationMode)}>
        <SelectTrigger className="border-none shadow-none h-auto py-0 px-0 text-xs focus:ring-0 w-auto gap-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="normal" className="text-xs">{normalLabel}</SelectItem>
          <SelectItem value="retractation" className="text-xs">{retractationLabel}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
